import {datadogRum} from '@datadog/browser-rum';
import $ from 'jquery';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {
  PAPER_SIZE_INFO,
  PaperSize,
  PRINT_PAGE_INFO,
  PrintOrientation,
  SAFARI_PAGE_INFO,
} from 'notebook/mediocre/print/PrintConstants';
import {SCROLLBAR_WIDTH} from 'notebook/tables/TableConstants';
import {
  ELN_PRINT_LAYOUT,
  ELN_PRINT_LAYOUT_WIDTH_WARNING_TEXT,
  VisibilityOptions,
} from 'settings/visibility-options/VisibilityOptions';

const DATA_SCALE_FOR_PRINT_ATTR = 'data-scale-for-print';

interface Margins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface PageDims {
  ppi: number;
  cssName: string;
  margins: Margins;
  width: number;
  height: number;
}

/**
 * Differentiate between headlessChrome rendering and regular browser rendering.
 *
 * When ELN_ENABLE_HEADLESS_BROWSER_NAVIGATOR_DETECTION is enabled, we use the standard
 * navigator.webdriver property which is set to true in Playwright/automation browsers.
 *
 * Legacy behavior: we manually set the headlessChrome user agent to be "Chrome" instead.
 * See benchling/exports/human_exports/headless_browser.py
 *
 * TODO(BNCH-139345): Remove legacy detection logic and user agent comment after flag cleanup
 * When we remove the flag we either need to add the userAgent check or rename the function to
 * isHeadlessBrowser and confirm that the call points do not use this to determine if this is
 * an export (which should be checked via isExportView and/or isPrintPreview depending on the case).
 *
 * @returns boolean indicating whether the current environment is headlessChrome
 */
export const isHeadlessChrome = (): boolean => {
  if (BenchlingConfig.ELN_ENABLE_HEADLESS_BROWSER_NAVIGATOR_DETECTION) {
    return navigator.webdriver === true;
  }
  return window.chrome == null && /\bChrome/.test(navigator.userAgent);
};

/**
 * Return the dimensions of the page (in pixels), accounting for ppi and portrait / landscape.
 *
 * NOTE: Safari has asymetric margins and preserves them on orientation change, so we match that behavior.
 *
 * @typedef {Object} PageDims
 * @property {number} ppi - pixels per inch used by browser when printing
 * @property {number} width - width of page in px
 * @property {number} height - height of page in px
 * @property {Object} margins - margins of page {top, right, bottom, left} in px.
 *
 * @param  {string} paperSize - value in PrintConstants.PaperSize
 * @param  {orientation} - value in PrintConstants.PrintOrientation
 * @return {PageDims}
 */
export function getPageDims(paperSize: PaperSize, orientation: PrintOrientation): PageDims {
  const {width, height, cssName} = PAPER_SIZE_INFO[paperSize];
  const {ppi, margins} = !benchlingUtil.safari ? PRINT_PAGE_INFO : SAFARI_PAGE_INFO;
  const portrait = orientation === PrintOrientation.PORTRAIT;
  return {
    ppi,
    cssName,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    margins: _.mapObject(margins, (inches) => (inches * ppi) as any) as Margins,
    width: (portrait ? width : height) * ppi,
    height: (portrait ? height : width) * ppi,
  };
}

export const _DOM = {
  /**
   * Return the dimensions of the bounding rectangle of the given node.
   *
   * Note that when run in NodeJS, JSDOM's Element.getBoundingClientRect() returns all 0s as it does not
   * have a layout engine. Instead, we compute the dimensions via data attributes:
   *  - A node's left is computed as the sum of data-printable-left-margin across all parent nodes
   *  - A node's width is read directly off its data-printable-layout-width.
   *  - A node's height is not readily computable, so we return 0. Any code that cares about height must
   *    find some workaround (e.g. see scaleAndRotateCSS).
   * @param  {Element} node
   * @return {Object} - {left, width, height} in pixels
   */
  getNodeRect(node: Element): {left: number; width: number; height: number} {
    let left: number;
    let width: number;
    let height: number;
    if (global.__NODE__) {
      left = 0;
      $(node)
        .parents('[data-printable-left-margin]')
        .each((i, parentNode) => {
          left += parseInt(parentNode.getAttribute('data-printable-left-margin') || '0', 10);
        });
      width = parseInt(node.getAttribute('data-printable-layout-width') || '0', 10);
      height = 0;
    } else {
      ({left, width, height} = node.getBoundingClientRect());
    }
    return {left, width, height};
  },

  /**
   * Construct the cssText for setting the orientation / margin of the page (for print)
   * NOTE: Safari ignores this. A Safari user will have to manually change page orientation when printing :-(
   *
   * @param {PageDims} pageDims
   * @return {string}
   */
  pageCSS(pageDims: PageDims): string {
    const {cssName, width, height, margins} = pageDims;
    const orientation = width > height ? 'landscape' : 'portrait';
    // Using {size: ${width}px ${height}px} doesn't seem to work (even mm), but {size: A4} does.
    return `
      @page {
        size: ${cssName} ${orientation};
        margin: ${margins.top}px ${margins.right}px ${margins.bottom}px ${margins.left}px;
      }
    `;
  },

  /**
   * Construct the cssText for scaling a DOM by a factor of `scale`.
   *
   * If we are running in node, it is safe that `height` of 0 is specified, as CSS `zoom` affects layout size
   * when opened in a non-Firefox browser (i.e. chrome headless).
   *
   * For Firefox, CSS `zoom` is not supported, so we must use CSS `transform: scale(...)`. Because this does
   * not affect layout size, we must manually trick the layout engine by adjusting CSS `margin-bottom` to
   * subtract out the scaled `height`. We do not use this "standards-compatible" solution due to buggy
   * behavior around layouts in at least Chrome / Chrome headless (BNCH-9044).
   *
   * @param  {string} id - the `data-scale-for-print` attribute value corresponding to the target element
   * @param  {number} scale - scale factor between 0 and 1 for CSS scale transform
   * @param  {number} width - original width of the target element
   * @param  {number} height  - original height of the target element
   * @return {string}
   */
  scaleCSS(id: string, scale: number, width: number, height: number): string {
    // Firefox does not support zoom: https://bugzilla.mozilla.org/show_bug.cgi?id=390936
    if (benchlingUtil.firefox) {
      return `
        @media print {
          [${DATA_SCALE_FOR_PRINT_ATTR}="${id}"] {
            margin-bottom: ${height * scale - height}px;
            transform: scale(${scale}, ${scale});
            transform-origin: top left;
          }
        }
      `;
    }
    // But all other browsers do: https://developer.mozilla.org/en-US/docs/Web/CSS/zoom#Browser_compatibility
    return `
      @media print {
        [${DATA_SCALE_FOR_PRINT_ATTR}="${id}"] {
          zoom: ${scale * 100}%;
        }
      }
    `;
  },

  /**
   * Construct the cssText for rotating and scaling a DOM node to fit an entire page.
   *
   * If we are running in node, we are unable to calculate the rendered height, and so `height` is 0.
   * In such cases, we don't try to preserve flow with the next item in the DOM and simply push
   * it to the next page via {page-break-after: always}.
   *
   * @param  {string} id - the `data-scale-for-print` attribute value corresponding to the target element
   * @param  {number} scale - scale factor between 0 and 1 for CSS scale transform
   * @param  {number} width - original width of the target element
   * @param  {number} height  - original height of the target element
   * @return {string}
   */
  scaleAndRotateCSS(id: string, scale: number, width: number, height: number): string {
    // Using margin-bottom on a transformed DOM object to add separation seems to cause a bug in Chrome
    // where the DOM object bleeds through. Since padding does not turn negative, keeping it margin-bottom
    // for that case.
    const bottomPx = height ? width * scale - height : 0;
    let bottomSpacing: string;
    if (bottomPx >= 0) {
      bottomSpacing = `padding-bottom: ${bottomPx}px;`;
    } else {
      bottomSpacing = `margin-bottom: ${bottomPx}px;`;
    }
    return `
      @media print {
        [${DATA_SCALE_FOR_PRINT_ATTR}="${id}"] {
          ${bottomSpacing}
          page-break-after: ${height ? 'auto' : 'always'};
          page-break-before: always;
          transform: scale(${scale}, ${scale}) translateY(${width}px) rotate(-90deg);
          transform-origin: top left;
        }
      }
    `;
  },
};

export function _assignScaleForPrintId(itemNode: HTMLElement): string {
  if (!itemNode.hasAttribute(DATA_SCALE_FOR_PRINT_ATTR)) {
    itemNode.setAttribute(DATA_SCALE_FOR_PRINT_ATTR, _.uniqueId('print'));
  }
  return itemNode.getAttribute(DATA_SCALE_FOR_PRINT_ATTR)!;
}

/**
 * Return CSS for print that optionally scales, rotates, and page-breaks each MediocreItem with class
 * .printable-container-scaleForPrint inside of a PrintableWrapper so that it can fit on the page, closest
 * to its original size.
 *
 * Only works in environments that do CSS layouts (e.g. browsers, not JSDOM)
 *
 * An element can be physically scaled down by a numerical factor (e.g. 0.5 to scale it to half the size);
 * as of Apr 2017, there's no way to scale an element to a target width. To determine this numerical factor,
 * we must know what (1) the current width of our table and (2) the page dimensions.
 *   (1) is known: we can meaure the table at render time.
 *   (2) is passed in, specified by the user via the PrintOptionsModal. Ideally we can detect this when the
 *       user changes the dimensions from the print dialog (but our detection is restricted to CSS w/o JS).
 *       Unfortunately, there isn't a reliable way of accessing the page dimensions via CSS (due to a bug in
 *       Chrome, see: https://crbug.com/489521). There also doesn't seem to be a reliable way to detect the
 *       pixels per inch when printing (Chrome is 96ppi while Safari is 90ppi)
 *
 * @param {HTMLElement} printableNode - DOM Node corresponding to a PrintableWrapper
 * @param {PageDims} pageDims
 * @param {boolean} shouldFitToWidth - whether or not the items should be transformed
 * @return {HTMLStyleElement} - a <style> to be attached to the DOM before print occurs
 */
export function getFitToWidthStyle(
  printableNode: Element,
  pageDims: PageDims,
  shouldFitToWidth: boolean
): JSX.Element {
  let cssText = '';

  if (printableNode.querySelector('.printable-container')) {
    cssText += _DOM.pageCSS(pageDims);
  } else {
    shouldFitToWidth = false;
  }

  let numResizedItems = 0;
  let numUnresizedItems = 0;
  if (shouldFitToWidth) {
    const boundingBoxNode = printableNode.querySelector('.printable-container-boundingBox');
    assert(boundingBoxNode != null, 'Unable to find .printable-container-boundingBox');
    const boundingBoxRect = _DOM.getNodeRect(boundingBoxNode);

    Array.from(printableNode.querySelectorAll('.printable-container-scaleForPrint')).forEach((node) => {
      // Assign an identifier so that we can apply CSS (with media queries) to the item.
      const itemNode = $(node).closest('.mediocre-item')[0] || node;
      const id = _assignScaleForPrintId(itemNode);

      // .printable-container--scaledForPrint has {width: max-content} to measure full width.
      const {width} = _DOM.getNodeRect(node);
      // .mediocre-item is the full item to scale (e.g. it may add a caption below) so account for its height.
      const {left, height} = _DOM.getNodeRect(itemNode);

      // itemNode is to be scaled / rotated within the bounding box of printableNode.
      // To avoid truncation, account for the amount (in pixels) the item is indented past the left margin.
      const leftIndent = left - boundingBoxRect.left;

      let marginH = pageDims.margins.left + pageDims.margins.right;
      const marginV = pageDims.margins.top + pageDims.margins.bottom;

      // In JSDOM we can't rely on the width, especially when the scaleForPrint component is a child;
      // i.e. a Run contains an output table. We therefore use the width of the child and
      // add some constant to the margin in the scale calculation.
      if ($(node).closest('.resultsAttachment-container, .resultsAttachment-draft-container')[0]) {
        marginH += 35;
      }
      // The scaling factor that would make the item's width equal to the width of the page.
      const horizontalScale = (pageDims.width - marginH - leftIndent) / width;

      // The scaling factor that would make the item's width (i.e., its height after rotating) equal to the
      // height of the page.
      const rotatedVerticalScale = Math.min((pageDims.height - marginV - pageDims.ppi / 2) / width, 1);

      // If the item is already narrower than the available width, we don't need to scale it.
      if (horizontalScale >= 1) {
        numUnresizedItems++;
        return;
      }
      numResizedItems++;
      if (shouldRotateItem(horizontalScale, rotatedVerticalScale, height, pageDims, marginH)) {
        cssText += _DOM.scaleAndRotateCSS(id, rotatedVerticalScale, width, height);
      } else {
        cssText += _DOM.scaleCSS(id, horizontalScale, width, height);
      }
    });
  }

  datadogRum.addAction('notebook.PrintUtil.getFitToWidthStyle.completed', {
    numResizedItems,
    numUnresizedItems,
  });
  return (
    <style
      type="text/css"
      // oxlint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{__html: cssText}}
    />
  );
}

export function shouldRotateItem(
  horizontalScale: number,
  rotatedVerticalScale: number,
  height: number,
  pageDims: {width: number},
  horizontalMargin: number
): boolean {
  // NOTE (BNCH-92393): explicitly disable rotation for headless-chrome exports for now
  // we disable rotation for headless-chrome exports to allow worksheet exports to achieve
  // parity with entry exports. Entry exports using nodeshell do not have item rotation
  // since nodeshell always has "window" height = 0
  // revisit this code when we do want to re-enable large item rotation for exports
  if (isHeadlessChrome()) {
    return false;
  }

  if (horizontalScale > 0.5) {
    // Not extreme enough for rotating to be worthwhile.
    return false;
  }

  if (horizontalScale > rotatedVerticalScale) {
    // Rotating would make the scale more extreme.
    return false;
  }

  if (height === 0) {
    // Height is always zero in jsdom. Since we lack height information, we can't rotate without risking the
    // end of the table running off the right side of the page.
    return false;
  }

  const newHorizontalLength = height * rotatedVerticalScale;
  const availableHorizontalSpace = pageDims.width - horizontalMargin;

  // Ensure the end of the table doesn't run off the right side of the page.
  return newHorizontalLength < availableHorizontalSpace;
}

// Equivalent to the `@mediocre-page-width` LESS variable. Note that this calculation is not perfect for all cases since
// some browsers like Safari use a different PPI value. Ideally this would use `getPageDims` to properly account for
// the calculation, but going to maintain the pre-existing logic for now.
const PIXELS_REQUIRED_FOR_US_LETTER_PAGE = PAPER_SIZE_INFO[PaperSize.US_LETTER].width * PRINT_PAGE_INFO.ppi;

export function maybeUpdatePrintWarningForContentWidth(
  containerWidth: number,
  visibilityOptions: VisibilityOptions | null
): void {
  if (visibilityOptions != null) {
    const renderPrintLayoutWarning = containerWidth < PIXELS_REQUIRED_FOR_US_LETTER_PAGE + SCROLLBAR_WIDTH;
    const printLayoutVisibilityOption = visibilityOptions.getOption(ELN_PRINT_LAYOUT.name)!;
    printLayoutVisibilityOption?.set(
      'cssWarning',
      renderPrintLayoutWarning ? ELN_PRINT_LAYOUT_WIDTH_WARNING_TEXT : undefined
    );
  }
}
